function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { cloneElement } from 'react';
import { ensureArray } from '@deephaven/utils';
import { getItemTextValue, isItemElement, isSectionElement } from "../utils/index.js";
/**
 * Flat shape used internally by the `MultiSelect` keyboard / filter / state
 * hooks. Not part of the public API.
 */
/**
 * Section-preserving shape used internally for client-side filtering. Not
 * part of the public API.
 */
/**
 * React prepends `.$` to user-supplied keys in the element's `.key` property.
 * Strip that prefix so we get the original key value.
 */
function cleanReactKey(rawKey) {
  return rawKey.replace(/^\.\$/, '');
}

/**
 * Convert a JSX `<Item>` element into a flat `{key, label}` record. Returns
 * `null` for items without a key.
 */
function itemElementToFlat(element) {
  var _getItemTextValue;
  if (element.key == null) {
    return null;
  }
  var key = cleanReactKey(String(element.key));
  var label = (_getItemTextValue = getItemTextValue(element)) !== null && _getItemTextValue !== void 0 ? _getItemTextValue : typeof element.props.children === 'string' ? element.props.children : key;
  return {
    kind: 'item',
    key,
    label
  };
}

/**
 * Type guard that checks whether a flat entry is a section.
 */
export function isFlatSection(entry) {
  return entry.kind === 'section';
}

/**
 * Walk a JSX <Item>/<Section> children array and return a
 * section-preserving flat representation suitable for the internal
 * filter / keyboard / state hooks. Children are expected to have already
 * been normalized via `wrapItemChildren` so primitives have been wrapped
 * into `<Item>` elements.
 */
export function flattenJsxChildren(children) {
  var entries = [];
  children.forEach(child => {
    if (isSectionElement(child)) {
      var section = child;
      var sectionKey = section.key != null ? cleanReactKey(String(section.key)) : String(entries.length);
      var sectionItems = [];
      ensureArray(section.props.children).forEach(sectionChild => {
        if (isItemElement(sectionChild)) {
          var item = itemElementToFlat(sectionChild);
          if (item != null) {
            sectionItems.push(item);
          }
        }
      });
      entries.push({
        kind: 'section',
        key: sectionKey,
        title: section.props.title,
        items: sectionItems
      });
      return;
    }
    if (isItemElement(child)) {
      var item = itemElementToFlat(child);
      if (item != null) {
        entries.push(item);
      }
    }
  });
  return entries;
}

/**
 * Flatten a section-preserving entry list into a plain MultiSelectFlatItem[],
 * extracting items out of any sections.
 */
export function flattenEntriesToItems(entries) {
  return entries.flatMap(entry => isFlatSection(entry) ? entry.items : [entry]);
}

/**
 * Filter entries by search text. Items within sections are filtered
 * individually; sections with no matching items are removed.
 */
export function filterEntries(entries, text, contains) {
  var result = [];
  entries.forEach(entry => {
    if (isFlatSection(entry)) {
      var filtered = entry.items.filter(item => contains(item.label, text));
      if (filtered.length > 0) {
        result.push(_objectSpread(_objectSpread({}, entry), {}, {
          items: filtered
        }));
      }
    } else if (contains(entry.label, text)) {
      result.push(entry);
    }
  });
  return result;
}

/**
 * Collect the set of all surviving item keys from a list of flat entries.
 * Used to drive filterJsxChildrenByKeys so we don't re-walk JSX with the
 * search text predicate.
 */
export function collectEntryItemKeys(entries) {
  var keys = new Set();
  entries.forEach(entry => {
    if (isFlatSection(entry)) {
      entry.items.forEach(item => keys.add(item.key));
    } else {
      keys.add(entry.key);
    }
  });
  return keys;
}
export function filterJsxChildrenByKeys(children, survivingKeys) {
  var result = [];
  children.forEach(child => {
    if (isSectionElement(child)) {
      var section = child;
      var filteredChildren = filterJsxChildrenByKeys(ensureArray(section.props.children).filter(c => c != null), survivingKeys);
      if (filteredChildren.length > 0) {
        result.push(/*#__PURE__*/cloneElement(section, {
          children: filteredChildren
        }));
      }
      return;
    }
    if (isItemElement(child)) {
      var item = child;
      if (item.key != null && survivingKeys.has(cleanReactKey(String(item.key)))) {
        result.push(child);
      }
    }
  });
  return result;
}
export function resolveSelection(selection, allKeys) {
  if (selection == null) {
    return new Set();
  }
  if (selection === 'all') {
    return new Set(allKeys);
  }
  return new Set([...selection].map(String));
}
//# sourceMappingURL=multiSelectUtils.js.map